import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/common/advance-table/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableFooter from 'components/common/advance-table/AdvanceTableFooter_paging';
import AdvanceTablePagination from 'components/common/advance-table/AdvanceTablePagination_paging';
import { useNavigate } from 'react-router-dom';
import CheckOutModal from 'components/modal/CheckOutModal';
import paths from 'routes/paths';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faExternalLinkAlt, faBuilding } from '@fortawesome/free-solid-svg-icons';
import AdminPasswordAlertModal from 'components/modal/AdminPasswordAlertModal';
import { useAppContext } from 'providers/AppProvider';
import styled from 'styled-components';

// --- Styled Components ---
const PageContainer = styled.div`
  padding: 2rem 1rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c3d4fe 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  font-size: 1.5rem;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.1);
`;

const PageTitle = styled.h2`
  margin: 0;
  font-weight: 700;
  color: #1e293b;
  font-size: 1.75rem;
  letter-spacing: -0.5px;
`;

const PageSubtitle = styled.p`
  margin: 4px 0 0 0;
  color: #64748b;
  font-size: 0.95rem;
`;

const StyledCard = styled(Card)`
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04), 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  }
`;
import {
  requestSiteToken,
  fetchCompanyList,
  deleteMultiCompanyInfo
} from 'hooks/CallApi'; // API 함수 가져오기
import { formatDate } from 'components/common/SunnyUtils';

function AdvanceTableExample() {
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [passwordModalShow, setPasswordModalShow] = useState(false);
  const [modalReShow, setModalReShow] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize, setPageSize] = useState(10); // 초기 페이지 사이즈 설정
  const [loading, setLoading] = useState(true); // 로딩 상태
  const [showSpinner, setShowSpinner] = useState(false); // 스피너 상태 추가
  const [currentPage, setCurrentPage] = useState(0); // 현재 페이지
  const [perPage, setPerPage] = useState(10); // 페이지당 항목 수 (size)

  const [ErrorCode, setErrorCode] = useState('');
  const [ErrorMsg, setErrorMsg] = useState('');
  const [modalErrorShow, setModalErrorShow] = useState(false);

  const {
    config: { checkorg, siteToken, userId, isNavbarVerticalCollapsed },
    setConfig
  } = useAppContext();

  const columns = [
    {
      accessorKey: 'num',
      header: '순번',
      meta: { maxlength: 3, headerProps: { className: 'text-900' } },
      enableSorting: false
    },
    {
      accessorKey: 'clientName',
      header: '고객사명',
      meta: { maxlength: 15, headerProps: { className: 'text-900' } },
      enableSorting: false
    },
    {
      accessorKey: 'clientPhone',
      header: '고객사연락처',
      meta: { maxlength: 20, headerProps: { className: 'text-900' } },
      enableSorting: false
    },
    {
      accessorKey: 'clientEmail',
      header: '고객사이메일',
      meta: { maxlength: 40, headerProps: { className: 'text-900' } },
      enableSorting: false
    },
    {
      accessorKey: 'clientAddress',
      header: '고객사주소',
      meta: { maxlength: 50, headerProps: { className: 'text-900' } },
      enableSorting: false
    },
    {
      accessorKey: 'createdAt',
      header: '등록일시',
      meta: { maxlength: 19, headerProps: { className: 'text-900' } },
      enableSorting: false
    },
    {
      accessorKey: 'dashboard',
      enableSorting: false,
      header: '관리',
      cell: ({ row }) => (
        <Button
          variant="outline-primary"
          size="sm"
          id={`status-switch-${row.id}`}
          className="d-flex align-items-center gap-1"
          style={{
            fontSize: '0.75rem',
            padding: '4px 10px',
            borderRadius: '6px',
            whiteSpace: 'nowrap',
          }}
          onClick={event => {
            event.stopPropagation();
            handleStatusChange(row);
          }}
        >
          <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.7rem' }} />
          관리 콘솔
        </Button>
      ),
      meta: { headerProps: { className: 'text-900' } }
    }

  ];

  const table = useAdvanceTable({
    data: tableData,
    columns,
    sortable: true,
    selection: true,
    pagination: true,
    perPage,
    selectionColumnWidth: 30
  });

  // API 호출 함수
  const loadData = async (page, size) => {
    const spinnerTimer = setTimeout(() => setShowSpinner(true), 300);
    setLoading(true);

    try {
      const response = await fetchCompanyList(page, size);

      // 인증 진행 중이면 조용히 리턴
      if (!response || !response.data) {
        console.log('Authentication in progress, skipping data load');
        return;
      }

      const logData = response.data.content.map((item, index) => ({
        num: page * size + index + 1,
        createdAt: item.insertTimeUtc
          ? formatDate(item.insertTimeUtc)
          : '-',
        updateAt: item.updateTimeUtc
          ? formatDate(item.updateTimeUtc)
          : '-',
        imgUrl: item.imgUrl,
        active: item.active,
        description: item.description,
        clientName: item.name,
        clientEmail: item.email,
        clientPhone: item.tel,
        clientAddress: item.address,
        companyId: item.companyId,
        // Include pricing policy information
        pricingPolicyUuid: item.pricingPolicyUuid,
        pricingPolicyName: item.pricingPolicyName
      }));

      setTotalElements(response.data.page.totalElements);
      setTableData(logData);
    } catch (error) {
      console.error('API 데이터 로드 에러:', error);
    } finally {
      clearTimeout(spinnerTimer);
      setLoading(false);
      setShowSpinner(false);
    }
  };

  // 페이지 변경 시 데이터 다시 로드
  useEffect(() => {
    setConfig('checkorg', '');
    setConfig('isNavbarVerticalCollapsed', true);
    loadData(currentPage, perPage);
  }, [currentPage, perPage]);

  // 페이지네이션 이벤트 핸들러
  const handlePageChange = newPage => {
    setCurrentPage(newPage);
    table.resetRowSelection(); // 선택된 행 초기화
  };

  const handlePerPageChange = newPerPage => {
    setPerPage(newPerPage); // 페이지 크기 업데이트
    setCurrentPage(0); // 첫 페이지로 이동
  };

  const handleRowClick = row => {
    const {
      createdAt,
      updateAt,
      imgUrl,
      active,
      description,
      clientName,
      clientEmail,
      clientPhone,
      clientAddress,
      companyId,
      pricingPolicyUuid,
      pricingPolicyName
    } = row.original;

    navigate(`/safedb/SA/adminSites/edit`, {
      state: {
        createdAt,
        updateAt,
        imgUrl,
        active,
        description,
        clientName,
        clientEmail,
        clientPhone,
        clientAddress,
        companyId,
        pricingPolicyUuid,
        pricingPolicyName
      }
    });
  };

  const handleDeleteClick = () => {
    setModalShow(true);
  };

  const handleConfirmDelete = async () => {
    // setPasswordModalShow(true);
    const selectedCompanyIds = table
      .getSelectedRowModel()
      .rows.map(row => row.original.companyId); // 선택된 행의 companyId 가져오기

    try {
      await deleteMultiCompanyInfo(selectedCompanyIds); // 삭제 API 호출
      setModalReShow(true);

      table.resetRowSelection(); // 선택 초기화
    } catch (error) {
      setErrorCode(error.response.data.code);
      setErrorMsg(error.response.data.message);
      setModalErrorShow(true);
    }
    setModalShow(false);

  };

  const handleConfirmPassword = () => {
    setPasswordModalShow(false);
    setModalReShow(true);
  };

  const selectedRowCount = table.getSelectedRowModel().rows.length;

  const handleDeleteRe = () => {
    setModalReShow(false);
    navigate('/safedb/SA/adminSites');
  };

  const handleStatusChange = async row => {
    const { companyId, clientName } = row.original;

    // 🎯 로딩 인디케이터 시작
    setShowSpinner(true);
    document.body.style.cursor = 'wait'; // 마우스 커서를 로딩으로 변경

    try {
      const refreshToken = sessionStorage.getItem('refreshToken');
      const impersonationData = {
        username: userId,
        refreshToken,
        companyId
      };

      console.log(`🔄 [${clientName}] 사이트 토큰 발급 시작...`);
      const startTime = Date.now();

      const { token } = await requestSiteToken(impersonationData);

      const endTime = Date.now();
      console.log(`✅ [${clientName}] 사이트 토큰 발급 완료 (${endTime - startTime}ms)`);

      sessionStorage.setItem('siteToken', token); // 새로 발급받은 토큰을 세션에 저장

      // siteToken에서 실제 companyId 추출하여 config에 저장
      try {
        const tokenPayload = JSON.parse(atob(token.split('.')[1]));
        const actualCompanyId = tokenPayload.cou || companyId; // cou 필드가 companyId
        setConfig('checkorg_companyId', actualCompanyId); // 토큰에서 추출한 실제 companyId 저장
        console.log('siteToken에서 추출한 companyId (cou):', actualCompanyId);
      } catch (error) {
        console.warn('siteToken 디코딩 실패, 요청한 companyId 사용:', error);
        setConfig('checkorg_companyId', companyId); // 디코딩 실패 시 원래 companyId 사용
      }

      setConfig('checkorg', clientName); // 선택된 고객사 이름 설정
      setConfig('isNavbarVerticalCollapsed', false); // 선택된 고객사 이름 설정

      navigate('/');
    } catch (error) {
      console.error('❌ 관리 모드로 전환하는데 실패했습니다:', error);

      // 인증 에러인 경우 특별 처리
      if (error.message === 'AUTHENTICATION_REQUIRED') {
        console.log('🔒 인증이 필요합니다. 로그인 프로세스를 진행하세요.');
        // 스피너 중지하고 사용자에게 메시지 표시 (리다이렉트는 이미 getTokenHeaders에서 처리됨)
        return;
      }

      // 기타 에러 처리 (필요시 사용자에게 에러 메시지 표시)
      setErrorCode('TOKEN_ERROR');
      setErrorMsg('고객사 토큰 발급에 실패했습니다. 다시 시도해주세요.');
      setModalErrorShow(true);
    } finally {
      // 🎯 로딩 인디케이터 종료
      setShowSpinner(false);
      document.body.style.cursor = 'default'; // 마우스 커서를 기본으로 복원
    }
  };
  return (
    <AdvanceTableProvider {...table}>
      {/* 🎯 로딩 오버레이 */}
      {showSpinner && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            flexDirection: 'column'
          }}
        >
          <Spinner animation="border" variant="light" size="lg" />
          <div style={{ color: 'white', marginTop: '1rem', fontSize: '1.1rem' }}>
            고객사 토큰 발급 중...
          </div>
        </div>
      )}

      <StyledCard>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1.25rem 1.5rem', backgroundColor: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>

        <AdvanceTableFooter
          rowsPerPageSelection
          onPageChange={(page, newPageSize) => {
            handlePerPageChange(newPageSize); // 페이지 크기 변경
            setCurrentPage(page); // 현재 페이지 설정
          }}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
        >
          {/* Excel 버튼 - 나중에 활성화 가능하도록 주석 처리 */}
          {/*
          <Button
            variant="success"
            style={{
              width: '100px'
            }}
            disabled={selectedRowCount === 0}
          >
            <FontAwesomeIcon
              icon={faFileExcel}
              style={{ marginRight: '5px' }}
            />
            Excel
          </Button>
          */}
          {selectedRowCount > 0 ? (
            <Button
              variant="danger"
              style={{
                width: '100px',
                borderRadius: '8px',
                fontWeight: 700,
                boxShadow: '0 2px 4px rgba(239, 68, 68, 0.2)'
              }}
              onClick={handleDeleteClick}
            >
              삭제
            </Button>
          ) : (
            <Link to={paths.addadminSites} style={{ textDecoration: 'none' }}>
              <Button
                variant="success"
                style={{
                  width: '100px',
                  borderRadius: '8px',
                  fontWeight: 700,
                  boxShadow: '0 2px 4px rgba(16, 185, 129, 0.2)'
                }}
              >
                등록
              </Button>
            </Link>
          )}
        </div>
      </div>
      {loading ? (
        showSpinner ? (
          <div className="d-flex justify-content-center align-items-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <p></p>
        )
      ) : (
        <AdvanceTable
          headerClassName="bg-light text-nowrap align-middle text-800"
          rowClassName="align-middle white-space-nowrap border-bottom border-200"
          tableProps={{
            hover: true,
            className: 'fs-10 mb-0 overflow-hidden text-700'
          }}
          onRowClick={handleRowClick}
        />
      )}
        <Card.Footer className="bg-light py-3 border-top">
          <div className="fs--1 fw-semi-bold text-500 mb-2">
            총 <span className="text-primary fw-bold">{totalElements.toLocaleString()}</span>건
          </div>
          <div className="d-flex justify-content-center">
            <AdvanceTablePagination
              currentPage={currentPage}
              totalItems={totalElements}
              itemsPerPage={perPage}
              onChange={newPage => handlePageChange(newPage)}
            />
          </div>
        </Card.Footer>
      </StyledCard>

      <CheckOutModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        onConfirm={handleConfirmDelete}
        title="경고"
        body="선택하신 고객사에 대한 모든 설정값 및 권한 정보가 삭제됩니다. 정말 삭제하시겠습니까?"
        cancelText="취소"
        confirmText="삭제"
        variant="warning"
        CancelButton={true}
        SubmitButton={true}
      />
      <AdminPasswordAlertModal
        show={passwordModalShow}
        onHide={() => setPasswordModalShow(false)}
        onConfirm={handleConfirmPassword}
      />
      <CheckOutModal
        show={modalReShow}
        onHide={() => setModalReShow(false)}
        onConfirm={handleDeleteRe}
        title="완료"
        body="삭제가 완료 되었습니다."
        confirmText="확인"
        variant="success"
        SubmitButton={true}
      />
      <CheckOutModal
        show={modalErrorShow}
        onHide={() => setModalErrorShow(false)}
        onConfirm={() => setModalErrorShow(false)}
        title="경고"
        // body={`에러코드 : ${ErrorCode}, 에러 메시지 : ${ErrorMsg}`}
        body={`${ErrorMsg}`}
        cancelText="취소"
        confirmText="확인"
        variant="warning"
        SubmitButton={true}
      />
    </AdvanceTableProvider>
  );
}

const adminSites = () => {
  return (
    <PageContainer>
      <HeaderSection>
        <TitleWrapper>
          <IconWrapper>
            <FontAwesomeIcon icon={faBuilding} />
          </IconWrapper>
          <div>
            <PageTitle>고객사 관리</PageTitle>
            <PageSubtitle>플랫폼을 이용 중인 고객사 목록을 관리합니다.</PageSubtitle>
          </div>
        </TitleWrapper>
      </HeaderSection>
      <AdvanceTableExample />
    </PageContainer>
  );
};

export default adminSites;
